import { useMemo } from 'react';

import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { DocumentManagement } from './document-management';
import { Issues } from './issues';
import { Metadata } from './metadata';
import { RelativitySync } from './relativity-sync';
import { RunHistory } from './run-history';
import { FailedDocuments } from './run-history/:runId';
import { UploadHistory } from './upload-history';
import { UploadFailures } from './upload-history/:uploadId';
import { Users } from './users';
import { MatterType } from '../../../../../../../backend/src/types/types';
import { PageHeader } from '../../../../components/page-header';
import { PageTitle } from '../../../../components/page-title';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { Tabs, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

enum SettingsTab {
  Metadata = 'metadata',
  Issues = 'issues',
  DocumentManagement = 'document-management',
  RelativitySync = 'relativity-sync',
  RunHistory = 'run-history',
  UploadHistory = 'upload-history',
  Users = 'users',
}

const tabs = [
  {
    id: SettingsTab.Metadata,
    title: 'Metadata',
    icon: <Icon className="lucide--info" />,
  },
  {
    id: SettingsTab.Issues,
    title: 'Issues',
    icon: <Icon className="lucide--pen-line" />,
  },
  {
    id: SettingsTab.DocumentManagement,
    title: 'Document Management',
    icon: <Icon className="lucide--folder-open" />,
  },
  {
    id: SettingsTab.RelativitySync,
    title: 'Relativity Sync',
    icon: <Icon className="lucide--refresh-cw" />,
  },
  {
    id: SettingsTab.Users,
    title: 'Users',
    icon: <Icon className="lucide--users-round" />,
  },
  {
    id: SettingsTab.RunHistory,
    title: 'Run History',
    icon: <Icon className="lucide--file-clock" />,
  },
  {
    id: SettingsTab.UploadHistory,
    title: 'Upload History',
    icon: <Icon className="lucide--upload" />,
  },
];

export function MatterSettings() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { data: matter } = useGetMatterV2(matterId);

  const location = useLocation();

  const isPersonalMatter = matter?.type === MatterType.Personal;

  const visibleTabs = useMemo(() => {
    if (isPersonalMatter) {
      return tabs.filter((tab) => tab.id !== SettingsTab.Issues && tab.id !== SettingsTab.Users);
    }
    return tabs;
  }, [isPersonalMatter]);

  const activeTab = useMemo(() => {
    const pathSegments = location.pathname.split('/');

    for (const tab of visibleTabs) {
      if (pathSegments.includes(tab.id)) {
        return tab.id;
      }
    }
  }, [location.pathname, visibleTabs]);

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${matter.id}`,
                  label: matter.name,
                  active: true,
                  iconClass: 'lucide--folder',
                },
                { href: `/matters/${matter.id}/settings/metadata`, label: 'Settings', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text="Matter Settings" />
        </PageHeader>

        <Tabs className="mb-2" value={activeTab}>
          <TabsList className="w-full justify-start gap-4">
            {visibleTabs.map((tab) => (
              <TabsTrigger
                asChild
                key={tab.id}
                value={tab.id}
                className="pb-2.5 text-gray-700 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500"
              >
                <Link to={tab.id}>{tab.title}</Link>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </PageContainer>

      <div className="flex h-full flex-col overflow-y-auto px-10 py-4">
        <Routes>
          <Route path="metadata" element={<Metadata />} />
          {!isPersonalMatter && <Route path="issues" element={<Issues />} />}
          <Route path="document-management" element={<DocumentManagement />} />
          <Route path="relativity-sync" element={<RelativitySync />} />
          <Route path="run-history/:runId" element={<FailedDocuments />} />
          <Route path="run-history" element={<RunHistory />} />
          <Route path="upload-history/:uploadId" element={<UploadFailures />} />
          <Route path="upload-history" element={<UploadHistory />} />
          {!isPersonalMatter && <Route path="users" element={<Users />} />}
        </Routes>
      </div>
    </>
  );
}
